import React from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  required,
  useNotify,
  useRedirect,
  useRecordContext,
  useDataProvider,
  useRefresh,
  Datagrid,
  TextField,
  ArrayField,
  FunctionField,
} from 'react-admin';
import {
  Typography,
  Button as MuiButton,
  Divider,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { AddFormsToCategoryDialog } from './AddFormsToCategoryDialog';
import { useState } from 'react';

interface Form {
  id: string;
  name: string;
}

interface WorkflowCategory {
  id: string;
  name: string;
  workflowId: string;
  forms: Form[];
}

const WorkflowCategoryEditForm = () => {
  const record = useRecordContext<WorkflowCategory>();
  const redirect = useRedirect();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const [isAddFormDialogOpen, setIsAddFormDialogOpen] = useState(false);

  if (!record) {
    return null;
  }

  const handleSave = async (values: { name: string }) => {
    try {
      await dataProvider.update('task-categories', {
        id: record.id,
        data: {
          name: values.name,
        },
        previousData: record,
      });
      notify('Category updated');
      redirect(`/workflows/${record.workflowId}?tab=2`);
    } catch (error) {
      notify('Error: Could not update category', { type: 'warning' });
    }
  };

  const handleDeleteForm = async (formId: string) => {
    const updatedForms = record.forms.filter((form) => form.id !== formId);
    try {
      await dataProvider.update('task-categories', {
        id: record.id,
        data: {
          forms: updatedForms.map(f => f.id),
        },
        previousData: record,
      });
      notify('Form removed');
      refresh();
    } catch (error) {
      notify('Error: Could not remove form', { type: 'warning' });
    }
  };

  return (
    <>
    <SimpleForm onSubmit={handleSave}>
      <MuiButton
        variant="outlined"
        onClick={() => redirect(`/workflows/${record.workflowId}?tab=2`)}
        size="small"
      >
        Back to Workflow
      </MuiButton>
      <div style={{ padding: '16px 0' }} />

      <Typography variant="h6" gutterBottom>
        Edit Category
      </Typography>
      <div style={{ padding: '8px 0' }} />
      <TextInput source="name" validate={required()} fullWidth />

      <Divider sx={{ my: 1 }} />

      <Typography variant="h6" gutterBottom>
        Associated Forms
      </Typography>
      <div style={{ padding: '8px 0' }} />

      {record.forms && record.forms.length > 0 ? (
        <ArrayField source="forms" record={record}>
          <Datagrid
            bulkActionButtons={false}
            sx={{
              width: '100%',
              minWidth: 0,
              '& .RaDatagrid-headerCell': { backgroundColor: 'grey.100' },
              '& .column-actions': { width: 100 }, // Add fixed width for actions column
            }}
          >
            <TextField source="name" label="Name" />
            <FunctionField
              label="Actions"
              source="actions" // Add source prop to properly identify column
              render={(record: Form) => (
                <IconButton
                  onClick={() => handleDeleteForm(record.id)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            />
          </Datagrid>
        </ArrayField>
      ) : (
        <Typography sx={{ mt: 2, color: 'text.secondary' }}>
          No forms associated with this category.
        </Typography>
      )}

        <div style={{ padding: '10px 0' }} />
        <MuiButton
          variant="outlined"
          onClick={() => setIsAddFormDialogOpen(true)}
          size="small"
          sx={{ mb: 2 }}
        >
          Add Forms
        </MuiButton>
    </SimpleForm>
      <AddFormsToCategoryDialog
        open={isAddFormDialogOpen}
        onClose={() => setIsAddFormDialogOpen(false)}
        categoryId={record.id}
        existingForms={record.forms}
      />
    </>
  );
};

export const WorkflowCategoryEdit = () => {
  return (
    <Edit title="Edit Category">
      <WorkflowCategoryEditForm />
    </Edit>
  );
};
